import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type {
  ClientCreated,
  ClientDetail,
  ClientSummary,
  InviteDelivery,
  MessagePack,
  MessagePackResult,
  PlanChangeResult,
  PlanQuote,
  ResellerAlert,
  ResellerPlan,
  ResellerApplied,
  ResellerProfile,
  TopupOrder,
  TopupOrderSummary,
  TopupResult,
  TransactionKind,
  UpgradeRequest,
  WalletTransactionPage,
} from './types'

import { api } from '@/lib/api/client'
import { keys } from '@/lib/api/keys'

const base = '/reseller/v1'

export const resellerApi = {
  me: () => api.get<ResellerProfile>(`${base}/me`),
  plans: () => api.get<ResellerPlan[]>(`${base}/plans`),
  packs: () => api.get<MessagePack[]>(`${base}/message-packs`),

  ledger: (params: { kind?: TransactionKind | undefined; cursor?: string | undefined }) =>
    api.get<WalletTransactionPage>(`${base}/wallet/transactions`, { ...params, limit: 50 }),
  createTopup: (amountMinor: number) =>
    api.post<TopupOrder>(`${base}/wallet/topups`, { amount_minor: amountMinor }),
  confirmTopup: (body: {
    razorpay_order_id: string
    razorpay_payment_id?: string
    razorpay_signature?: string
  }) => api.post<TopupResult>(`${base}/wallet/topups/confirm`, body),
  apply: (body: { business_name: string; phone?: string }) =>
    api.post<ResellerApplied>(`${base}/apply`, body),
  topups: () => api.get<TopupOrderSummary[]>(`${base}/wallet/topups`),
  invoicePdf: (invoiceId: string) => api.download(`${base}/invoices/${invoiceId}/pdf`),
  failTopup: (orderId: string, reason: string) =>
    api.post<void>(`${base}/wallet/topups/${encodeURIComponent(orderId)}/failed`, { reason }),

  clients: (search: string) => api.get<ClientSummary[]>(`${base}/clients`, { search }),
  client: (id: string) => api.get<ClientDetail>(`${base}/clients/${id}`),
  createClient: (body: {
    company_name: string
    owner_email: string
    plan_key?: string
    owner_password?: string
  }) =>
    api.post<ClientCreated>(`${base}/clients`, body),
  resendInvite: (id: string) => api.post<InviteDelivery>(`${base}/clients/${id}/invite`, {}),
  quote: (id: string, planKey: string) =>
    api.post<PlanQuote>(`${base}/clients/${id}/plan/quote`, { plan_key: planKey }),
  changePlan: (id: string, planKey: string) =>
    api.post<PlanChangeResult>(`${base}/clients/${id}/plan`, { plan_key: planKey }),
  cancelScheduled: (id: string) => api.delete<void>(`${base}/clients/${id}/plan/scheduled`),
  deactivate: (id: string) => api.post<ClientDetail>(`${base}/clients/${id}/deactivate`, {}),
  reactivate: (id: string) => api.post<ClientDetail>(`${base}/clients/${id}/reactivate`, {}),
  setOwnerPassword: (id: string, password: string) =>
    api.post<{ status: 'updated' | 'created' }>(`${base}/clients/${id}/owner-password`, { password }),
  buyPack: (id: string, packKey: string) =>
    api.post<MessagePackResult>(`${base}/clients/${id}/message-topups`, { pack_key: packKey }),

  requests: (status: string) =>
    api.get<UpgradeRequest[]>(`${base}/upgrade-requests`, { status }),
  approve: (id: string) => api.post<PlanChangeResult>(`${base}/upgrade-requests/${id}/approve`, {}),
  decline: (id: string, note: string) =>
    api.post<void>(`${base}/upgrade-requests/${id}/decline`, { note: note || null }),

  alerts: () => api.get<ResellerAlert[]>(`${base}/alerts`, { limit: 100 }),
  readAll: () => api.post<void>(`${base}/alerts/read`, {}),
}

// ── Queries ──────────────────────────────────────────────────────────────────

export function useResellerMe(options: { retry?: boolean } = {}) {
  return useQuery({
    queryKey: keys.reseller.me(),
    queryFn: resellerApi.me,
    retry: options.retry ?? false,
    staleTime: 30_000,
  })
}

export function useResellerPlans() {
  return useQuery({ queryKey: keys.reseller.plans(), queryFn: resellerApi.plans, staleTime: 5 * 60_000 })
}

export function useMessagePacks() {
  return useQuery({ queryKey: keys.reseller.packs(), queryFn: resellerApi.packs, staleTime: 5 * 60_000 })
}

export function useClients(search: string) {
  return useQuery({
    queryKey: keys.reseller.clients(search),
    queryFn: () => resellerApi.clients(search),
  })
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: keys.reseller.client(id ?? ''),
    queryFn: () => resellerApi.client(id!),
    enabled: Boolean(id),
  })
}

export function useQuote(clientId: string, planKey: string | null) {
  return useQuery({
    queryKey: keys.reseller.quote(clientId, planKey ?? ''),
    queryFn: () => resellerApi.quote(clientId, planKey!),
    enabled: Boolean(planKey),
    retry: false,
    staleTime: 0,
  })
}

export function useLedger(kind: TransactionKind | undefined) {
  return useInfiniteQuery({
    queryKey: keys.reseller.ledger({ kind }),
    queryFn: ({ pageParam }) => resellerApi.ledger({ kind, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  })
}

export function useUpgradeRequests(status: string) {
  return useQuery({
    queryKey: keys.reseller.requests(status),
    queryFn: () => resellerApi.requests(status),
  })
}

export function useResellerAlerts() {
  return useQuery({ queryKey: keys.reseller.alerts(), queryFn: resellerApi.alerts })
}

// ── Mutations ────────────────────────────────────────────────────────────────

/** Anything that moves money or changes a plan touches most of the panel. */
function useInvalidateReseller() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: keys.reseller.all })
}

export function useCreateClient() {
  const invalidate = useInvalidateReseller()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: resellerApi.createClient,
    onSuccess: () => {
      // The new company joins the reseller's workspace list too.
      void qc.invalidateQueries({ queryKey: keys.workspaces.all })
      return invalidate()
    },
  })
}

export function useChangePlan(clientId: string) {
  const invalidate = useInvalidateReseller()
  return useMutation({
    mutationFn: (planKey: string) => resellerApi.changePlan(clientId, planKey),
    onSuccess: invalidate,
  })
}

export function useDeactivateClient(clientId: string) {
  const invalidate = useInvalidateReseller()
  return useMutation({ mutationFn: () => resellerApi.deactivate(clientId), onSuccess: invalidate })
}

export function useReactivateClient(clientId: string) {
  const invalidate = useInvalidateReseller()
  return useMutation({ mutationFn: () => resellerApi.reactivate(clientId), onSuccess: invalidate })
}

export function useSetOwnerPassword(clientId: string) {
  const invalidate = useInvalidateReseller()
  return useMutation({
    mutationFn: (password: string) => resellerApi.setOwnerPassword(clientId, password),
    onSuccess: invalidate,
  })
}

export function useCancelScheduled(clientId: string) {
  const invalidate = useInvalidateReseller()
  return useMutation({
    mutationFn: () => resellerApi.cancelScheduled(clientId),
    onSuccess: invalidate,
  })
}

export function useBuyPack(clientId: string) {
  const invalidate = useInvalidateReseller()
  return useMutation({
    mutationFn: (packKey: string) => resellerApi.buyPack(clientId, packKey),
    onSuccess: invalidate,
  })
}

export function useResendInvite(clientId: string) {
  const invalidate = useInvalidateReseller()
  return useMutation({ mutationFn: () => resellerApi.resendInvite(clientId), onSuccess: invalidate })
}

export function useApproveRequest() {
  const invalidate = useInvalidateReseller()
  return useMutation({ mutationFn: resellerApi.approve, onSuccess: invalidate })
}

export function useDeclineRequest() {
  const invalidate = useInvalidateReseller()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => resellerApi.decline(id, note),
    onSuccess: invalidate,
  })
}

export function useReadAllAlerts() {
  const invalidate = useInvalidateReseller()
  return useMutation({ mutationFn: resellerApi.readAll, onSuccess: invalidate })
}

export function useInvalidateWallet() {
  return useInvalidateReseller()
}

export function useTopupOrders() {
  return useQuery({ queryKey: keys.reseller.topups(), queryFn: resellerApi.topups })
}
